import { cn } from "../../../app/components/primitives/utils";

// A short row of mutually exclusive choices that all stay visible: the period, and what to break the
// spend down by. A dropdown hides the answer to "what am I looking at?"; three or four options are
// better all on show. Each choice is a toggle button inside a labelled group, so a screen reader hears
// which one is on.

export interface SegmentedOption<T extends string> {
  id: T;
  label: string;
  /** A small count after the label, such as how many sites there are. */
  count?: number;
}

export function SegmentedControl<T extends string>({
  label,
  options,
  value,
  onChange,
  className,
}: {
  label: string;
  options: SegmentedOption<T>[];
  value: T;
  onChange: (id: T) => void;
  className?: string;
}) {
  return (
    <div
      role="group"
      aria-label={label}
      className={cn(
        "inline-flex max-w-full rounded-md border p-0.5",
        className,
      )}
    >
      {options.map((option) => (
        <button
          key={option.id}
          type="button"
          aria-pressed={option.id === value}
          onClick={() => onChange(option.id)}
          className={cn(
            "min-w-0 flex-1 whitespace-nowrap rounded px-3 py-1.5 text-sm font-medium outline-none transition-colors focus-visible:ring-[3px] focus-visible:ring-ring/50",
            option.id === value
              ? "bg-primary text-primary-foreground"
              : "text-muted-foreground hover:text-foreground",
          )}
        >
          {option.label}
          {option.count !== undefined && (
            <span
              className={cn(
                "ml-1.5 text-xs tabular-nums",
                option.id === value
                  ? "text-primary-foreground/80"
                  : "text-muted-foreground",
              )}
            >
              {option.count}
            </span>
          )}
        </button>
      ))}
    </div>
  );
}
